import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { BookImage, CalendarDays } from "lucide-react";
import { Spinner } from "../components/common";

/* Public, read-only trip memory page — /memories/{token}.
   No sign-in required; the owner can revoke the link at any time. */

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(`${d}T12:00:00`);
  return isNaN(dt) ? d : dt.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
};

export default function Memories() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/journal/shared/${token}`)
      .then((r) => setData(r.data)).catch(() => setErr(true));
  }, [token]);

  if (err) return (
    <div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="memories-invalid">
      <BookImage className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
      <h1 className="font-heading font-900 text-2xl mb-2">This memory link is no longer available</h1>
      <p className="text-muted-foreground text-sm">The owner may have stopped sharing it. <Link to="/" className="text-primary hover:underline">Go to TravelerPulse</Link></p>
    </div>
  );
  if (!data) return <Spinner full />;

  const trip = data.trip || {};
  const dates = [trip.start_date, trip.end_date].filter(Boolean).join(" – ");
  const start = trip.start_date;
  const dayNo = (d) => {
    if (!d || !start) return null;
    const n = Math.round((new Date(`${d}T12:00:00`) - new Date(`${start}T12:00:00`)) / 86400000) + 1;
    return n >= 1 ? n : null;
  };

  return (
    <div className="max-w-3xl mx-auto px-5 py-10" data-testid="memories-page">
      <div className="text-center border-b border-zinc-200 pb-8 mb-8">
        <div className="text-xs font-mono uppercase tracking-widest text-[#7c3aed] font-700 mb-2">Trip Memory · TravelerPulse</div>
        <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight" data-testid="memories-title">{trip.title || "A Trip to Remember"}</h1>
        {dates && <div className="mt-2 inline-flex items-center gap-1.5 text-muted-foreground text-sm"><CalendarDays className="w-4 h-4" /> {dates}</div>}
      </div>

      {(data.entries || []).length === 0 && (
        <p className="text-center text-muted-foreground text-sm">This journal doesn't have any entries yet.</p>
      )}

      <div className="space-y-10">
        {(data.entries || []).map((e) => {
          const n = dayNo(e.date);
          return (
            <article key={e.id} data-testid={`memories-entry-${e.id}`}>
              <div className="text-xs font-mono uppercase tracking-widest text-[#7c3aed] font-700">
                {n ? `Day ${n} · ` : ""}{fmtDate(e.date)}
              </div>
              {e.title && <h2 className="font-heading font-900 text-2xl mt-1">{e.title}</h2>}
              {e.text && <p className="text-base text-zinc-700 whitespace-pre-wrap mt-2">{e.text}</p>}
              {(e.photos || []).length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                  {e.photos.map((url, i) => (
                    <img key={i} src={`${BACKEND}${url}`} alt={e.title || "Trip memory"} loading="lazy"
                      className="w-full h-40 sm:h-48 object-cover border border-zinc-200" />
                  ))}
                </div>
              )}
            </article>
          );
        })}
      </div>

      <div className="mt-14 border-t border-zinc-200 pt-6 text-center text-xs text-muted-foreground">
        Made with <Link to="/travel-hub" className="text-[#7c3aed] font-700 hover:underline">My Travel Hub</Link> on TravelerPulse — plan trips, track expenses and keep memories.
      </div>
    </div>
  );
}
